import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form } from 'antd'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'

import {
  useStocksServiceConfirmEntryEndpoint,
  useStocksServiceCreateEntryEndpoint,
  useStocksServiceGetEntryListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  CreateInventoryEntryItemDto,
  DocumentServiceDto
} from 'src/api/requests'
import { FormFooter, message, PageBreadcrumbs } from 'src/components'
import { InputCreateForm } from 'src/features/stock/components'
import { useFormStyles } from 'src/features/stock/styles'
import {
  useFileHandler,
  useScrollToFirstError,
  ValidationError
} from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

export const StockInputsCreatePage = () => {
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const navigate = useNavigate()
  const { t } = useLocales()

  const { styles } = useFormStyles()
  const { onFileChange, saveFiles, isUploading } = useFileHandler({
    form,
    folder: `inputs`
  })
  const { onScrollToError } = useScrollToFirstError(form)

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [useStocksServiceGetEntryListEndpointKey]
    })
    onBackClick()
  }

  const rawDeliveryDate = form.getFieldValue('deliveryDate')
  const formattedDeliveryDate = dayjs(rawDeliveryDate).format(yearMonthDaySlash)

  const { mutate, isPending } = useStocksServiceCreateEntryEndpoint({
    onSuccess: async (data) => {
      message.success(t('stock_administration.entry_added_success'))

      await acceptMutation({
        id: data.id,
        requestBody: {
          deliveryDate: formattedDeliveryDate
        }
      })
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutate: acceptMutation } = useStocksServiceConfirmEntryEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('stock_administration.accepted_success'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const onBackClick = () => {
    form.resetFields()
    navigate(paths.inputs.root)
  }

  const onSave = async () => {
    try {
      const values = await form.validateFields()

      const items = values?.medicine?.map(
        (item: CreateInventoryEntryItemDto) => ({
          productId: item?.productId,
          barCode: item?.barCode,
          expirationDate: dayjs(item?.expirationDate).format(yearMonthDaySlash),
          productionDate: dayjs(item?.productionDate).format(yearMonthDaySlash),
          quantity: item?.quantity,
          unitPrice: item?.unitPrice,
          measurementUnitId: item?.measurementUnitId,
          tvaUnitRateId: item?.tvaUnitRateId
        })
      )

      const pharmaceutics = values?.consumables?.map(
        (item: CreateInventoryEntryItemDto) => ({
          productId: item?.productId,
          barCode: item?.barCode,
          expirationDate: dayjs(item?.expirationDate).format(yearMonthDaySlash),
          productionDate: dayjs(item?.productionDate).format(yearMonthDaySlash),
          quantity: item?.quantity,
          unitPrice: item?.unitPrice,
          measurementUnitId: item?.measurementUnitId,
          tvaUnitRateId: item?.tvaUnitRateId
        })
      )

      const services = values?.services?.map((service: DocumentServiceDto) => ({
        serviceId: service.serviceId,
        unitPrice: service.unitPrice
      }))

      const updatedFiles = await saveFiles()

      const payload = {
        contractId: values?.contractId,
        documentNumber: values?.invoiceNumber,
        documentDate: dayjs(values?.invoiceDate).format(yearMonthDaySlash),
        takeoverDate: dayjs(values?.takeoverDate).format(yearMonthDaySlash),
        transporterId: values?.transporterId,
        accompanyingNotice: values?.transporterAccompanyingNotice,
        comment: values?.comment,
        items: [...items, ...pharmaceutics],
        services,
        mediaItems: updatedFiles
      }

      mutate({ requestBody: payload })
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  return (
    <PageBreadcrumbs
      title={t('stock_administration.add_new_input')}
      items={[
        {
          title: t('menu.consumable_medicaments'),
          path: paths.inputs.root
        },
        {
          title: t('stock.inputs'),
          path: paths.inputs.root
        },
        {
          title: t('stock_administration.add_new_input')
        }
      ]}
    >
      <Flex vertical gap={24} className={styles.form}>
        <Button
          icon={<LeftOutlined />}
          className="fit-content"
          onClick={onBackClick}
        >
          {t('common.back')}
        </Button>

        <Form
          layout="vertical"
          form={form}
          requiredMark={false}
          scrollToFirstError
        >
          <InputCreateForm form={form} onChange={onFileChange} />

          <FormFooter
            onCancel={onBackClick}
            onSave={onSave}
            saveLabel={t('common.add')}
            saveConfirmTitle={`${t('common.add')}?`}
            loading={isUploading || isPending}
          />
        </Form>
      </Flex>
    </PageBreadcrumbs>
  )
}
